import qs from 'qs';
import { useCallback } from 'react';
import { type Location, Navigate, useLocation } from 'react-router';
import { useUser } from 'features/auth';
import { TokenLogin } from 'features/onboarding';
import { useGetFinalReferer, useOnSuccessLogin, useOnboardingNavigation } from '../hooks';
import type { OnboardingLocationState } from '../types';

export type TokenLoginLocationState = OnboardingLocationState;

const getQueryParameter = (location: Location, key: string) => {
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });

  const value = query[key];
  if (typeof value === 'string') {
    return value;
  }

  return undefined;
};

const PageTokenLogin = () => {
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- useLocation returns state as any
  const state: { referrer?: string } | undefined = location.state;

  const loginToken = getQueryParameter(location, 'login_token');
  const referrer = getQueryParameter(location, 'referrer');
  const userId = getQueryParameter(location, 'user_id');

  const getFinalReferer = useGetFinalReferer();

  // eslint-disable-next-line @typescript-eslint/unbound-method -- false positive: hooks return plain functions, not class methods, so there is no `this` to lose
  const { onSuccessLogin } = useOnSuccessLogin<TokenLoginLocationState>({ referrer });
  // eslint-disable-next-line @typescript-eslint/unbound-method -- false positive: hooks return plain functions, not class methods, so there is no `this` to lose
  const { onNavigateToCheckCode: onNavigateToCheckCodeBase } = useOnboardingNavigation<TokenLoginLocationState>();

  const onNavigateToCheckCode = useCallback(
    async ({ tokenToGetCode, tokenToLogin }: { tokenToGetCode?: string; tokenToLogin?: string }) => {
      await onNavigateToCheckCodeBase({ referrer, tokenToGetCode, tokenToLogin });
    },
    [onNavigateToCheckCodeBase, referrer],
  );

  const user = useUser({ requiredAuth: false });

  if (!loginToken || (userId && user?.railsUserData.id === userId)) {
    return <Navigate replace to={getFinalReferer({ referrer, state })} />;
  }

  return (
    <TokenLogin loginToken={loginToken} onNavigateToCheckCode={onNavigateToCheckCode} onSuccessLogin={onSuccessLogin} />
  );
};

export default PageTokenLogin;
